// auth.module.js
import AuthService from "../services/auth.service";

const user = JSON.parse(localStorage.getItem("user"));

const state = {
  permissions: [],
  settings: [],
  status: { loggedIn: Boolean(user) },
  user: user || null,
};

const getters = {
  userPermissions: (state) => state.permissions,
};

const actions = {
  async initializeApp({ state, dispatch }) {
    try {
      if (state.status.loggedIn && state.user) {
        // security/2.2b: always call checkToken on boot. It doubles as the
        // /me refresh under Sanctum SPA mode — returns the current user or
        // 401s (which the axios response interceptor catches and redirects
        // to /login). The prior `!state.user.expires_at` gate was Passport-
        // only bookkeeping and is meaningless now that expires_at is no
        // longer written to localStorage.
        await dispatch("checkToken");

        await dispatch("permissions", state.user);

        const email = state.user?.user?.email;
        if (email) {
          dispatch("identifyUserWithHubSpot", email);
        }
      }

      await dispatch("siteSettings");
    } catch (error) {
      console.error("App initialization error:", error);
    }
  },

  async login({ commit, dispatch }, credentials) {
    try {
      const response = await AuthService.login(credentials);

      commit("loginSuccess", response);

      if (response?.user?.email) {
        dispatch("identifyUserWithHubSpot", response?.user?.email);
      }

      return response;
    } catch (error) {
      commit("loginFailure");
      throw error;
    }
  },

  async loginAsUser({ commit, dispatch }, userData) {
    try {
      const response = await AuthService.loginAsUser(userData);

      commit("loginSuccess", response);

      if (response?.user?.email) {
        // Reset HubSpot identity
        dispatch("resetHubSpotIdentity");

        // Applied HubSpot identity
        dispatch("identifyUserWithHubSpot",  response?.user?.email);
      }

      return response;
    } catch (error) {
      commit("loginFailure");
      throw error;
    }
  },

  async logout({ commit, dispatch }, user) {
    try {
      const response = await AuthService.logout(user);

      // Reset HubSpot identity
      dispatch("resetHubSpotIdentity");

      commit("logoutSuccess");

      return response;
    } catch (error) {
      commit("logoutFailure");
      throw error;
    }
  },

  async register({ commit }, userData) {
    try {
      const response = await AuthService.register(userData);
      commit("registerSuccess");
      return response;
    } catch (error) {
      commit("registerFailure");
      throw error;
    }
  },

  forgot(_, user) {
    return AuthService.forgot(user);
  },

  async permissions({ commit }, user) {
    try {
      const response = await AuthService.permissions(user);
      const permissions = response.permissions;
      commit("setPermissions", permissions);
      return response;
    } catch (error) {
      console.error("Permission fetching error:", error);
      throw error;
    }
  },

  async checkToken({ state, commit }) {
    try {
      const response = await AuthService.checkToken();

      // security/2.2b: checkToken now returns `{valid: true, user: <fresh>}`
      // instead of `{valid, expires_at}`. Refresh the cached profile in
      // state.user.user (the nested User model inside the login response
      // envelope) and re-persist to localStorage so refreshes pick it up.
      // A missing `user` on the response (unexpected shape) is a no-op —
      // an actual expiry surfaces as a 401 that the axios response
      // interceptor already redirects to /login.
      if (response?.user) {
        const updatedUser = {
          ...state.user,
          user: response.user,
        };

        commit("updateUser", updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }

      return response;
    } catch (error) {
      console.error("Token check error:", error);
      throw error;
    }
  },

  async siteSettings({ commit }) {
    try {
      const response = await AuthService.siteSettings();
      const settings = response.settings;
      commit("setSiteSettings", settings);
      return response;
    } catch (error) {
      console.error("Settings fetching error:", error);
      throw error;
    }
  },

  resetHubSpotIdentity() {
    const platform = process.env.VUE_APP_PLATFORM;

    if (platform === "production") {
      const cookies = ["hubspotutk", "__hstc", "__hssc", "__hssrc"];

      cookies.forEach((cookie) => {
        document.cookie = `${cookie}=;path=/;expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
      });

    }
  },

  identifyUserWithHubSpot(_, email) {
    const platform = process.env.VUE_APP_PLATFORM;

    if (!email || platform !== "production") return;

    window._hsq = window._hsq || [];

    window._hsq.push([
      "identify",
      {
        email: email,
      },
    ]);

  },
};

const mutations = {
  loginSuccess(state, user) {
    state.status.loggedIn = true;
    state.user = user;
  },

  loginFailure(state) {
    state.status.loggedIn = false;
    state.user = null;
  },

  logoutSuccess(state) {
    state.status.loggedIn = false;
    state.user = null;
    state.permissions = [];
  },

  logoutFailure(state) {
    state.status.loggedIn = false;
    state.user = null;
    state.permissions = [];
  },

  registerSuccess(state) {
    state.status.loggedIn = true;
    state.user = user;
  },

  registerFailure(state) {
    state.status.loggedIn = false;
  },

  setPermissions(state, permissions) {
    state.permissions = permissions;
  },

  setSiteSettings(state, settings) {
    state.settings = settings;
  },

  updateUser(state, user) {
    state.user = user;
  },
};

export const auth = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};